import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectScriptCategoriesByCreator,
  selectScriptsByCreatorAndFilter,
  selectScriptsLoadingByCreatorAndFilter,
} from "../../../../redux/chatScript/selectors/scriptSelectors";
import ScriptCategories from "../../../../components/chatScripts/ScriptCategories/ScriptCategories";
import OrderScriptRow from "./OrderScriptRow";
import { REQUEST_LOADING_STATES } from "../../../../helpers/constant";
import { fetchScriptCategoriesByCreator, fetchScriptsByFilter } from "../../../../redux/chatScript/thunks/scriptThunks";
import Loader from "../../../../components/loader/Loader";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { chatScriptService } from "../../../../services/chat-script.service";
import { reorderScriptsCache, setScriptsForKey } from "../../../../redux/chatScript/slices/scriptSlice";
import useFetchUserInfos from "../../../../hooks/userHooks/useFetchUserInfos";
import "./EditScriptOrderView.less";

type OrderItem = { id: string; title: string; message: string; price?: number | null; type: "script" | "voice"; category?: string };

const EditScriptOrderView: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser } = useFetchUserInfos();
  const currentCreator = currentUser?.id;
  const categories = useAppSelector(selectScriptCategoriesByCreator(currentCreator!));
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [isJiggleMode, setIsJiggleMode] = useState<boolean>(false);
  const scriptsEntry = useAppSelector(selectScriptsByCreatorAndFilter(currentCreator!, activeCategory));
  const scriptsLoading = useAppSelector(selectScriptsLoadingByCreatorAndFilter(currentCreator!, activeCategory));
  const [items, setItems] = useState<OrderItem[]>([]);
  const [originalItems, setOriginalItems] = useState<OrderItem[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);
  const [dragCurrentY, setDragCurrentY] = useState<number>(0);
  const [dragOffsetY, setDragOffsetY] = useState<number>(0);
  const [draggedHeight, setDraggedHeight] = useState<number>(0);
  const listRectRef = useRef<DOMRect | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const rowHeights = useRef<Record<string, number>>({});
  const listRef = useRef<HTMLDivElement | null>(null);
  const autoScrollSpeedRef = useRef<number>(0);
  const autoScrollRafRef = useRef<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const lastRequestedPageRef = useRef<number>(0);

  useEffect(() => {
    if (currentCreator && categories.length === 0) {
      dispatch(fetchScriptCategoriesByCreator(currentCreator));
    }
  }, [dispatch, currentCreator, categories.length]);

  useEffect(() => {
    if (draggingId) return;
    const mapped = (scriptsEntry.items || []).map((s) => ({
      id: s._id,
      title: s.title,
      message: s.message,
      price: s.price ?? 0,
      type: "script" as const,
      category: activeCategory,
    }));
    const currentIds = items.map((i) => i.id).join("|");
    const nextIds = mapped.map((i) => i.id).join("|");
    if (currentIds !== nextIds) setItems(mapped);
    const origIds = originalItems.map((i) => i.id).join("|");
    if (origIds !== nextIds) setOriginalItems(mapped);
  }, [scriptsEntry.items, activeCategory, draggingId]);

  useEffect(() => {
    if (!currentCreator) return;
    lastRequestedPageRef.current = scriptsEntry.page || 0;
    if (!scriptsEntry || !scriptsEntry.items || scriptsEntry.items.length === 0) {
      lastRequestedPageRef.current = 1;
      dispatch(fetchScriptsByFilter({ creatorId: currentCreator, filterOrCategoryId: activeCategory, page: 1 }));
    }
  }, [dispatch, currentCreator, activeCategory]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const rootEl = listRef.current;
    if (!rootEl) return;
    if (!currentCreator) return;
    const MIN_PAGE_SIZE = 15;
    if ((scriptsEntry.items?.length || 0) < MIN_PAGE_SIZE) return;
    const isScrollable = rootEl.scrollHeight > rootEl.clientHeight;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        const page = scriptsEntry.page || 1;
        const totalPages = scriptsEntry.totalPages || 0;
        const pageSize = 15;
        const hasMore = totalPages > 0 ? page < totalPages : (scriptsEntry.items?.length || 0) % pageSize === 0;
        const isLoading = scriptsLoading === REQUEST_LOADING_STATES.PENDING;
        if (!first.isIntersecting || !hasMore || isLoading) return;
        const nextPage = page + 1;
        if (lastRequestedPageRef.current >= nextPage) return;
        lastRequestedPageRef.current = nextPage;
        dispatch(fetchScriptsByFilter({ creatorId: currentCreator, filterOrCategoryId: activeCategory, page: nextPage }));
      },
      { root: isScrollable ? rootEl : null, rootMargin: "0px 0px 400px 0px", threshold: 0 }
    );
    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scriptsEntry.items?.length, scriptsEntry.page, scriptsEntry.totalPages, scriptsLoading, currentCreator, activeCategory, dispatch]);

  useEffect(() => {
    if (!isJiggleMode) return;

    const handlePointerDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!listRef.current || !target) return;
      if (!listRef.current.contains(target)) {
        setIsJiggleMode(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsJiggleMode(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, { capture: true });
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, { capture: true } as any);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isJiggleMode]);

  const filteredScripts = useMemo(() => {
    const base = items;
    if (activeCategory === "all") return base;
    return base.filter((s) => (s.category || "").toLowerCase() === activeCategory.toLowerCase());
  }, [activeCategory, items]);

  const setRowRef = (id: string, el: HTMLDivElement | null) => {
    rowRefs.current[id] = el;
    if (el) {
      rowHeights.current[id] = el.getBoundingClientRect().height;
    } else {
      delete rowHeights.current[id];
    }
  };

  const onStartDrag = (id: string, startClientY: number) => {
    setDraggingId(id);
    setDragCurrentY(startClientY);
    const listEl = listRef.current;
    if (listEl) listRectRef.current = listEl.getBoundingClientRect();
    const el = rowRefs.current[id];
    const elRect = el?.getBoundingClientRect();
    const offset = elRect ? startClientY - elRect.top : 0;
    setDragOffsetY(offset);
    setDraggedHeight(elRect?.height || 0);
    const ids = filteredScripts.map((s) => s.id);
    setDragStartIndex(ids.indexOf(id));
  };

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: PointerEvent) => {
      setDragCurrentY(e.clientY);
      const listEl = listRef.current;
      const rect = listRectRef.current;
      if (listEl && rect) {
        const edge = 40;
        let speed = 0;
        if (e.clientY > rect.bottom - edge) {
          const dist = Math.min(edge, e.clientY - (rect.bottom - edge));
          speed = Math.ceil((dist / edge) * 12);
        } else if (e.clientY < rect.top + edge) {
          const dist = Math.min(edge, rect.top + edge - e.clientY);
          speed = -Math.ceil((dist / edge) * 12);
        }
        autoScrollSpeedRef.current = speed;
        if (autoScrollRafRef.current == null) {
          const tick = () => {
            const ls = listRef.current;
            if (ls && autoScrollSpeedRef.current !== 0) {
              ls.scrollTop += autoScrollSpeedRef.current;
            }
            autoScrollRafRef.current = requestAnimationFrame(tick);
          };
          autoScrollRafRef.current = requestAnimationFrame(tick);
        }
      }

      const ids = filteredScripts.map((s) => s.id);
      const currentIndex = ids.indexOf(draggingId);
      if (currentIndex === -1) return;
      const listRect = listRectRef.current;
      if (!listRect || !listRef.current) return;
      const relativeY = e.clientY - listRect.top + listRef.current.scrollTop;
      let acc = 0;
      let targetIndex = 0;
      for (let i = 0; i < filteredScripts.length; i++) {
        const id = filteredScripts[i].id;
        const h = rowHeights.current[id] || 0;
        const mid = acc + h / 2;
        if (relativeY < mid) {
          targetIndex = i;
          break;
        }
        acc += h;
        targetIndex = i + 1;
      }
      if (targetIndex !== currentIndex) {
        const itemId = draggingId;
        setItems((prev) => {
          const array = [...prev];
          const from = array.findIndex((x) => x.id === itemId);
          const beforeId = filteredScripts[targetIndex]?.id;
          array.splice(from, 1);
          if (beforeId) {
            const to = array.findIndex((x) => x.id === beforeId);
            array.splice(to, 0, prev.find((x) => x.id === itemId)!);
          } else {
            array.push(prev.find((x) => x.id === itemId)!);
          }
          return array;
        });
      }
    };
    const onUp = () => {
      const movedId = draggingId;
      const start = dragStartIndex;
      setDraggingId(null);
      setDragCurrentY(0);
      setDragOffsetY(0);
      setDraggedHeight(0);
      autoScrollSpeedRef.current = 0;
      if (autoScrollRafRef.current != null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      if (movedId && start != null && start >= 0) {
        const idsNow = filteredScripts.map((s) => s.id);
        const end = idsNow.indexOf(movedId);
        if (end >= 0 && end !== start) {
          reorderRange(start, end);
        }
      }
      setDragStartIndex(null);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp, { once: true });
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp as any);
    };
  }, [draggingId, filteredScripts, dragStartIndex]);

  const reorderRange = async (start: number, end: number) => {
    if (!currentCreator) return;
    const prevScripts = (scriptsEntry.items || []).slice();
    const filterKey = activeCategory;
    dispatch(reorderScriptsCache({ creatorId: currentCreator, filterOrCategoryId: filterKey, start, end }));
    setItems((prev) => {
      const arr = [...prev];
      if (start >= 0 && start < arr.length && end >= 0 && end < arr.length) {
        const moved = arr.splice(start, 1)[0];
        arr.splice(end, 0, moved);
      }
      return arr;
    });

    try {
      await chatScriptService.reorderScripts({
        creatorId: currentCreator,
        start,
        end,
        categoryId: filterKey === "all" ? "all" : filterKey,
      });
      setOriginalItems(items);
      message.success(t("order-updated"));
    } catch (e) {
      console.error("reorder failed", e);
      dispatch(setScriptsForKey({ creatorId: currentCreator, filterOrCategoryId: filterKey, items: prevScripts }));
      setItems(originalItems);
      message.error(t("back-error-message"));
    }
  };

  return (
    <div className="order-scripts-view">
      <div className="order-scripts-content">
        <div className="order-scripts-header">
          <h1 className="order-scripts-title">{t("edit-script-order-instruction")}</h1>
        </div>

        <div className="order-scripts-panel">
          <ScriptCategories
            creatorId={currentCreator!}
            categories={categories.map((c) => ({ _id: c._id, name: c.name }))}
            activeCategory={activeCategory}
            onSelect={setActiveCategory}
          />

          <div className="order-script-list" role="list" ref={listRef}>
            {filteredScripts.map((item) => {
              if (draggingId === item.id && draggedHeight) {
                return <div key={item.id} style={{ height: draggedHeight }} />;
              }
              return (
                <OrderScriptRow
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  message={item.message}
                  price={item.price}
                  isJiggling={isJiggleMode}
                  onLongPress={() => setIsJiggleMode(true)}
                  onStartDrag={onStartDrag}
                  setRowRef={setRowRef}
                />
              );
            })}
            {filteredScripts.length === 0 && <div className="order-script-list-empty">{t("no-scripts-in-category")}</div>}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </div>
          {scriptsLoading === REQUEST_LOADING_STATES.PENDING && filteredScripts.length > 0 && (
            <div className="loader-script-wrapper">
              <Loader />
            </div>
          )}
          {draggingId &&
            (() => {
              const dragItem = items.find((x) => x.id === draggingId);
              if (!dragItem || !listRectRef.current) return null;
              const style: React.CSSProperties = {
                position: "fixed",
                top: dragCurrentY - dragOffsetY,
                left: listRectRef.current.left,
                width: listRectRef.current.width,
                zIndex: 9999,
                pointerEvents: "none",
              } as React.CSSProperties;
              return (
                <OrderScriptRow
                  key={`drag-${dragItem.id}`}
                  id={dragItem.id}
                  title={dragItem.title}
                  message={dragItem.message}
                  price={dragItem.price}
                  isJiggling={true}
                  isDragging={true}
                  dragStyle={style}
                />
              );
            })()}
        </div>

        <div className="order-scripts-actions">
          <button type="button" className="order-script-button" onClick={() => navigate(-1)}>
            {t("finish")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditScriptOrderView;
